"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useRef, type ReactNode } from "react";
import Link from "next/link";
import { HelpCircle, PhoneCall, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { trackEvent } from "@/utils/analytics";
import { WeeksNotMonthsVisual } from "./WeeksNotMonthsVisual";
import { MvpPipeline } from "./MvpPipeline";
import { MvpDeliverables } from "./MvpDeliverables";
import { MvpFitContrast } from "./MvpFitContrast";
import { FaqVisual } from "./FaqVisual";

interface MvpDeferredSectionsProps {
  isItalian: boolean;
  bookingLink: string;
}

export default function MvpDeferredSections({
  isItalian,
  bookingLink,
}: MvpDeferredSectionsProps) {
  const reduce = useReducedMotion();
  // Micro-animazione "viva" della CTA finale: gate in-view continuo.
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, { margin: "-80px" });
  const ctaPulse = ctaInView && !reduce;

  const faqs: { question: string; answer: ReactNode }[] = isItalian
    ? [
        {
          question: "Quanto costa un MVP?",
          answer: (
            <>
              L'investimento per il primo mese è di{" "}
              <span className="text-primary font-medium">4.800€</span>: setup,
              architettura e prime funzionalità MVP. Nei mesi successivi
              pianifichiamo integrazioni e roadmap evolutiva in base ai dati
              reali.
            </>
          ),
        },
        {
          question: "Quali tecnologie usi?",
          answer: (
            <>
              Stack moderno e collaudato: Next.js o React per il frontend,
              Supabase o PostgreSQL per il backend, Tailwind CSS per lo styling,
              deploy su Vercel o altro. Tutto in TypeScript.
            </>
          ),
        },
        {
          question: "E se il mio MVP deve scalare?",
          answer: (
            <>
              Il codice che scrivo è pensato per crescere in modo progressivo.
              Imposto una{" "}
              <span className="text-primary font-medium">base solida</span> per
              supportare le prime fasi di crescita senza rifare tutto da zero.
            </>
          ),
        },
        {
          question: "Sviluppi anche app mobile?",
          answer: (
            <>
              Sì. Per il mobile possiamo partire con una PWA oppure sviluppare in
              Flutter. Lo decidiamo insieme in base alle esigenze.
            </>
          ),
        },
        {
          question: "Di chi è il codice?",
          answer: (
            <>
              Tutto il codice specifico del tuo prodotto (logica di business,
              interfaccia, database) è di tua{" "}
              <span className="text-primary font-medium">
                proprietà al 100%
              </span>
              . I componenti tecnici generici (autenticazione, configurazione
              dell'infrastruttura, pattern architetturali) possono essere
              riutilizzati in altri progetti, come avviene nella prassi standard
              dello sviluppo software.
            </>
          ),
        },
      ]
    : [
        {
          question: "How much does an MVP cost?",
          answer: (
            <>
              The investment for the first month is{" "}
              <span className="text-primary font-medium">€4,800</span>: setup,
              architecture, and initial MVP features. Following months focus on
              integrations and roadmap based on real data.
            </>
          ),
        },
        {
          question: "What technologies do you use?",
          answer: (
            <>
              Modern, proven stack: Next.js or React for frontend, Supabase or
              PostgreSQL for backend, Tailwind CSS for styling, deployment on
              Vercel or other. All in TypeScript.
            </>
          ),
        },
        {
          question: "What if my MVP needs to scale?",
          answer: (
            <>
              I write code to grow progressively. I set a{" "}
              <span className="text-primary font-medium">solid foundation</span>{" "}
              to support early growth phases without rebuilding from scratch.
            </>
          ),
        },
        {
          question: "Do you also build mobile apps?",
          answer: (
            <>
              Yes. For mobile we can start with a PWA or build in Flutter, based
              on your needs.
            </>
          ),
        },
        {
          question: "Who owns the code?",
          answer: (
            <>
              All code specific to your product (business logic, interface,
              database) is{" "}
              <span className="text-primary font-medium">100% yours</span>.
              Generic technical components (authentication, infrastructure
              setup, architectural patterns) may be reused across projects, as is
              standard practice in software development.
            </>
          ),
        },
      ];

  return (
    <>
      {/* Signature: contrasto temporale "Settimane, non mesi" */}
      <WeeksNotMonthsVisual isItalian={isItalian} />

      {/* Il processo: pipeline animata che si riempie allo scroll */}
      <MvpPipeline isItalian={isItalian} />

      {/* Cosa ricevi: pacchetto di handover + stack come marquee animato */}
      <MvpDeliverables isItalian={isItalian} />

      {/* Per chi e' / non e': contrasto a due lati */}
      <MvpFitContrast isItalian={isItalian} />

      <section className="section-padding py-24 md:py-32 bg-surface/50">
        <div className="container-wide">
          {/* Header a tutta larghezza SOPRA la griglia: cosi' la card a destra
              si allinea alle domande (non all'header). Icona ridotta, inline. */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 max-w-2xl text-left"
          >
            {/* Icona "?" prima del titolo. Allineato a sinistra ovunque. */}
            <h2 className="flex items-center justify-start gap-3 font-display text-h2 font-bold">
              <HelpCircle
                className="w-6 h-6 md:w-7 md:h-7 text-primary shrink-0"
                aria-hidden="true"
              />
              {isItalian ? "Domande frequenti" : "Frequently asked questions"}
            </h2>
          </motion.div>

          {/* Domande e card animata accostate a SINISTRA: la card sta subito
              accanto alle domande (non spinta a destra) e si allunga in altezza
              esattamente dall'inizio della prima domanda alla fine dell'ultima
              (items-stretch + h-full). */}
          <div className="flex flex-col gap-12 lg:flex-row lg:items-stretch lg:gap-10">
            {/* Colonna sinistra: accordion, allineato a sinistra come l'header */}
            <div className="w-full lg:max-w-md lg:shrink-0">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-left"
              >
                {/* Le barre partono a sinistra (nessun offset di barra). Il testo
                    della domanda dentro il trigger e' indentato (pl-[2.75rem])
                    per allinearsi alla "D" di "Domande frequenti" = larghezza
                    icona header + gap-3. Il chevron resta a destra. */}
                <Accordion type="single" collapsible className="max-w-md space-y-4">
                  {faqs.map((faq, index) => (
                    <AccordionItem
                      key={faq.question}
                      value={`faq-${index}`}
                      className="surface-card px-6 border-none"
                    >
                      <AccordionTrigger className="hover:no-underline py-5 text-left font-display text-base font-semibold [&>span]:pl-[2.75rem]">
                        <span>{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-5 pl-[2.75rem]">
                        <p>{faq.answer}</p>
                        {(faq.question === "Sviluppi anche app mobile?" ||
                          faq.question === "Do you also build mobile apps?") && (
                          <div className="mt-3 inline-flex items-center gap-2 text-xs text-primary">
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-primary/80 touch-manipulation"
                                >
                                  <HelpCircle className="w-3.5 h-3.5" />
                                  {isItalian
                                    ? "Cos'è una PWA?"
                                    : "What is a PWA?"}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                side="bottom"
                                align="start"
                                sideOffset={8}
                                collisionPadding={16}
                                className="w-[min(20rem,calc(100vw-2rem))] border border-primary/30 bg-surface/95 text-xs leading-relaxed text-foreground"
                              >
                                {isItalian
                                  ? "Una PWA è una web app installabile sul telefono, più veloce da rilasciare rispetto a un'app nativa."
                                  : "A PWA is an installable web app on mobile, usually faster to launch than a native app."}
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>

            {/* Companion animato. Su mobile/tablet: colonna unica centrata
                sotto le domande (h fissa per far vedere la scena). Su desktop:
                subito accanto alle domande, dimensione fissa. */}
            <div className="mx-auto h-[20rem] w-full max-w-md lg:mx-0 lg:h-[24rem] lg:w-[34rem] lg:max-w-none lg:shrink-0">
              <FaqVisual isItalian={isItalian} />
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding pt-24 pb-28 md:pt-32 md:pb-36">
        <div className="container-wide" ref={ctaRef}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="surface-card relative overflow-hidden p-8 md:p-12 text-left max-w-4xl"
          >
            {/* Glow signature che respira piano (opacity only), gate in-view */}
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/15 blur-3xl"
              initial={false}
              animate={ctaPulse ? { opacity: [0.5, 1, 0.5] } : { opacity: 0.6 }}
              transition={
                ctaPulse
                  ? { duration: 4, ease: "easeInOut", repeat: Infinity }
                  : { duration: 0.4 }
              }
            />

            <div className="relative">
              <span className="inline-flex items-center gap-2 font-mono text-label text-primary mb-4">
                <motion.span
                  initial={false}
                  animate={
                    ctaPulse ? { scale: [1, 1.2, 1] } : { scale: 1 }
                  }
                  transition={
                    ctaPulse
                      ? { duration: 2.4, ease: "easeInOut", repeat: Infinity }
                      : { duration: 0.3 }
                  }
                  className="inline-flex"
                >
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                </motion.span>
                {isItalian ? "Facciamo due chiacchiere" : "Let's have a chat"}
              </span>
              <h2 className="page-cta-title mb-4">
                {isItalian ? (
                  <>
                    Raccontami la tua idea, la{" "}
                    <span className="text-primary">portiamo online</span>
                  </>
                ) : (
                  <>
                    Tell me your idea, let's get it{" "}
                    <span className="text-primary">online</span>
                  </>
                )}
              </h2>
              <p className="text-body-lg text-muted-foreground mb-8 max-w-lg">
                {isItalian
                  ? "Una call gratuita di 30 minuti, senza impegno: capiamo se ha senso e da dove partire. Parli direttamente con me."
                  : "A free 30-minute call, no strings attached: we figure out if it makes sense and where to start. You talk directly with me."}
              </p>
              <Button
                variant="hero"
                size="xl"
                className="w-full sm:w-auto"
                asChild
              >
              <Link
                href={bookingLink}
                onClick={() =>
                  trackEvent("book_call_click", {
                    tool: "tidycal",
                    location: "cta_section",
                  })
                }
              >
                <span className="sm:hidden">
                  {isItalian ? "Prenota call" : "Book call"}
                </span>
                <span className="hidden sm:inline">
                  {isItalian
                    ? "Prenota una call iniziale"
                    : "Book a discovery call"}
                </span>
                <PhoneCall className="w-4 h-4" />
              </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
